import React, { useEffect, useState } from "react";
import { Button, Input, Select } from "antd";

import { controller } from "../../controller";
import "./carrierResolution.css";
import {
  correctionOptions,
  initialChoice,
  isNameValid,
  isPhoneValid,
  resolutionState,
} from "./carrierChoice";

/**
 * "Which carrier is this?" for one policy imported from the PMS or read off a
 * claim form — the same control on every screen that asks it (the PMS review
 * pop-up and the request wizard's policy step). See carrierChoice.js for the
 * states and choices.
 *
 * What it deliberately does NOT do any more: offer three weak scored guesses as
 * the answer. On prod, 52 of 57 wrong carriers were one of those guesses. Our
 * full carrier list only appears when someone says the match is wrong or that
 * the carrier is one we already have — a deliberate correction, not a default.
 *
 * Controlled: the parent owns `choice` and gets every change via `onChange`.
 * `conflict` is a parsed 409 from saving a new carrier (parseCarrierConflict).
 */
export default function CarrierResolution({ preview, choice, onChange, conflict, providers, getPopupContainer }) {
  const carrier = (preview && preview.carrier) || {};
  const state = resolutionState(preview);
  // The name as printed — by the PMS, or on the claim form the office uploaded.
  const fromForm = !!preview && preview.source === "claim_form";
  const raw = carrier.name_raw || carrier.sikka_carrier_name_raw;
  const [picking, setPicking] = useState(false);
  const [list, setList] = useState(providers || null);

  useEffect(function () { if (providers) setList(providers); }, [providers]);

  // Our carrier list is only needed for a correction; load it then.
  const needsList = picking || (state === "placeholder" && (!choice || choice.kind === "provider"));
  useEffect(
    function () {
      if (!needsList || list) return undefined;
      let cancelled = false;
      (async function () {
        try {
          const res = await controller.listInsuranceProviders();
          if (!cancelled) setList((res && res.data) || []);
        } catch (e) {
          console.error(e);
          if (!cancelled) setList([]);
        }
      })();
      return function () { cancelled = true; };
    },
    [needsList, list],
  );

  function pick(id) {
    onChange({ kind: "provider", id: id });
  }

  function startCreate(prefill) {
    setPicking(false);
    const base = initialChoice(preview);
    onChange({
      kind: "create",
      name: prefill === false ? "" : (base && base.kind === "create" ? base.name : raw || ""),
      phone: (base && base.kind === "create" ? base.phone : carrier.pms_phone) || "",
      confirmNew: false,
    });
  }

  function editCreate(patch) {
    onChange(Object.assign({}, choice, patch, { confirmNew: patch.confirmNew || false }));
  }

  const pmsLine = raw ? (
    <div className="cr-pms">
      {fromForm
        ? <>The claim form names this carrier <strong>{raw}</strong>.</>
        : <>Your practice management system calls this carrier <strong>{raw}</strong>.</>}
    </div>
  ) : null;

  const conflictBox = conflict && conflict.provider ? (
    <div className={"cr-conflict" + (conflict.kind === "exists" ? " is-exists" : "")}>
      <div>
        {conflict.kind === "exists"
          ? <>We already have <strong>{conflict.provider.name}</strong>.</>
          : <>This looks like <strong>{conflict.provider.name}</strong>, which we already have
            {conflict.reason && conflict.reason !== "similar name" ? <> — {conflict.reason}</> : null}.</>}
      </div>
      <div className="cr-actions">
        <Button size="small" type="primary" onClick={function () { pick(conflict.provider.id); }}>
          Use {conflict.provider.name}
        </Button>
        {conflict.kind === "maybe" ? (
          <Button size="small" onClick={function () { editCreate({ confirmNew: true }); }}>
            No, it's a different carrier
          </Button>
        ) : null}
      </div>
    </div>
  ) : conflict && conflict.message ? <div className="cr-conflict is-exists">{conflict.message}</div> : null;

  // --- Adding a carrier we don't have ---------------------------------------------
  if (choice && choice.kind === "create") {
    const nameOk = isNameValid(choice.name);
    const phoneOk = isPhoneValid(choice.phone);
    return (
      <div className="cr">
        {pmsLine}
        <div className="cr-lead">
          {state === "placeholder"
            ? "Add the carrier on the patient's card so the team can verify it:"
            : "We don't have this carrier yet — add it so the team can verify it:"}
        </div>
        <div className="cr-form">
          <label className="cr-label">
            Carrier name
            <Input
              value={choice.name}
              maxLength={128}
              placeholder="As it appears on the patient's card"
              onChange={function (e) { editCreate({ name: e.target.value }); }}
              status={!nameOk && choice.name ? "error" : undefined}
            />
          </label>
          <label className="cr-label">
            Phone number
            <Input
              value={choice.phone}
              maxLength={32}
              placeholder="The carrier's provider line, e.g. 1-800-555-0100"
              onChange={function (e) { editCreate({ phone: e.target.value }); }}
              status={choice.phone && !phoneOk ? "error" : undefined}
            />
          </label>
        </div>
        {!phoneOk ? <div className="cr-hint">A phone number with at least 10 digits is needed so the team can call.</div> : null}
        {conflictBox}
        <button type="button" className="cr-link" onClick={function () { setPicking(true); onChange(null); }}>
          It's a carrier we already have
        </button>
      </div>
    );
  }

  // --- Choosing from our carriers (a correction, or a placeholder) --------------------
  if (picking || state === "placeholder") {
    const options = correctionOptions(list || []);
    return (
      <div className="cr">
        {pmsLine}
        <div className="cr-lead">
          {state === "placeholder" && carrier.name_kind === "network"
            ? "That's the claims network, not the insurer. Choose the insurer on the patient's card:"
            : state === "placeholder"
              ? (fromForm ? "The claim form doesn't name the carrier. Choose it:" : "The PMS doesn't name the carrier. Choose it:")
              : "Choose the carrier on the patient's card:"}
        </div>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder={list ? "Search our carriers" : "Loading carriers…"}
          loading={!list}
          value={choice && choice.kind === "provider" ? choice.id : undefined}
          onChange={pick}
          options={options}
          style={{ width: "100%" }}
          getPopupContainer={getPopupContainer}
        />
        <div className="cr-row">
          <button type="button" className="cr-link" onClick={function () { startCreate(state !== "placeholder"); }}>
            Not listed? Add it
          </button>
          {picking && state !== "placeholder" ? (
            <button type="button" className="cr-link is-quiet" onClick={function () { setPicking(false); onChange(initialChoice(preview)); }}>
              Cancel
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // --- We know it: matched, suggested, or set by hand -----------------------------------
  const chosenId = choice && choice.kind === "provider" ? choice.id : null;
  const known =
    (carrier.matched && carrier.matched.id === chosenId && carrier.matched) ||
    (carrier.suggested && carrier.suggested.id === chosenId && carrier.suggested) ||
    ((list || []).find(function (p) { return p.id === chosenId; })) ||
    null;
  const knownName = known ? known.name : null;
  let lead;
  if (state === "locked") lead = <>Carrier set by hand: <strong>{knownName || "kept"}</strong>.</>;
  else if (state === "suggested" && carrier.suggested && carrier.suggested.id === chosenId) {
    lead = (
      <>
        This looks like <strong>{knownName}</strong>
        {carrier.suggested.reason ? <span className="cr-reason"> — {carrier.suggested.reason}</span> : null}.
        {" "}Approving confirms it.
      </>
    );
  } else lead = <>We know this as <strong>{knownName || "the carrier you chose"}</strong>.</>;

  return (
    <div className="cr">
      {pmsLine}
      <div className={"cr-lead" + (state === "suggested" ? " is-suggested" : "")}>{lead}</div>
      <button type="button" className="cr-link" onClick={function () { setPicking(true); }}>
        {state === "locked" ? "Change carrier" : "Wrong carrier?"}
      </button>
    </div>
  );
}
